import React, { useEffect, useMemo, useState } from "react";
import DOMPurify from "dompurify";
import Button from "../../components/Button/Button";
import SectionTitle from "../../components/ui/SectionTitle";
import { useNavigate } from "react-router-dom";
import { getGroupsHome } from "../../services/groupServices";
import { getSettings } from "../../services/settingsServices";
import { getAdherentsByGroup } from "../../services/dashboardServices";
import { getDojos } from "../../services/dojoServices";
import { useUser } from "./../Home/UserContext";
import { FiMapPin, FiX } from "react-icons/fi";

function cn(...c) {
  return c.filter(Boolean).join(" ");
}

/* =========================
   Couleurs (tailwind bg -> hex)
   ========================= */
const tailwindBgToHex = {
  "bg-red-400": "#f87171",
  "bg-orange-400": "#fb923c",
  "bg-indigo-400": "#818cf8",
  "bg-purple-400": "#a78bfa",
  "bg-green-400": "#4ade80",
  "bg-yellow-400": "#facc15",
  "bg-blue-400": "#60a5fa",
  "bg-pink-400": "#f472b6",
  "bg-gray-400": "#9ca3af",
  "bg-cyan-500": "#06b6d4",
  "bg-teal-400": "#2dd4bf",
  "bg-emerald-400": "#34d399",
  "bg-lime-400": "#a3e635",
  "bg-amber-600": "#d97706",
  "bg-rose-400": "#fb7185",
  "bg-violet-400": "#c084fc",
  "bg-sky-400": "#38bdf8",
  "bg-fuchsia-500": "#d946ef",
  "bg-zinc-400": "#a1a1aa",
  "bg-gray-500": "#6b7280",
};
const getHexColor = (twClass) => tailwindBgToHex[twClass] || "#e5e7eb";

function clamp(n, a, b) {
  return Math.max(a, Math.min(b, n));
}
function hexToRgb(hex) {
  const h = hex.replace("#", "").trim();
  const full =
    h.length === 3
      ? h
          .split("")
          .map((x) => x + x)
          .join("")
      : h;
  return {
    r: parseInt(full.slice(0, 2), 16),
    g: parseInt(full.slice(2, 4), 16),
    b: parseInt(full.slice(4, 6), 16),
  };
}
function rgbToHex({ r, g, b }) {
  const to = (v) => v.toString(16).padStart(2, "0");
  return `#${to(r)}${to(g)}${to(b)}`;
}
function lighten(hex, amount = 0.6) {
  const { r, g, b } = hexToRgb(hex);
  return rgbToHex({
    r: Math.round(r + (255 - r) * amount),
    g: Math.round(g + (255 - g) * amount),
    b: Math.round(b + (255 - b) * amount),
  });
}
function isDark(hex) {
  const { r, g, b } = hexToRgb(hex);
  const L = (0.2126 * r + 0.7152 * g + 0.0722 * b) / 255;
  return L < 0.55;
}

/* =========================
   Helpers data
   ========================= */

// Ne convertir en URL absolue que les fichiers côté serveur (/uploads/...)
const toAbsolute = (u) => {
  if (!u) return "";
  const s = String(u).trim();
  if (/^https?:\/\//i.test(s) || s.startsWith("data:") || s.startsWith("blob:"))
    return s;
  if (!s.startsWith("/uploads")) return s;
  const base = process.env.REACT_APP_API_URL?.replace(/\/$/, "") || "";
  return `${base}${s}`;
};

const teachersOf = (g) =>
  [
    g.enseignant1,
    g.enseignant2,
    g.enseignant3,
    g.enseignant4,
    g.enseignant5,
    g.enseignant6,
  ]
    .filter(Boolean)
    .map((s) => String(s).trim())
    .filter(Boolean);

const sanitizeHtml = (html) =>
  DOMPurify.sanitize(html || "", {
    USE_PROFILES: { html: true },
    FORBID_ATTR: ["style"],
  });

const normalizeDiscipline = (value) =>
  String(value || "")
    .trim()
    .toLowerCase();

function extractDisciplinesUnique(groupes) {
  const disciplines = new Set();

  (groupes || []).forEach((g) => {
    const d = String(g?.discipline_groupe || "").trim();
    if (d) disciplines.add(d);
  });

  return Array.from(disciplines).sort((a, b) => a.localeCompare(b, "fr"));
}

/** Extrait années uniques depuis annees_naissance */
function extractAnneesUnique(groupes) {
  const annees = new Set();

  (groupes || []).forEach((g) => {
    const champ = g.annees_naissance;
    if (!champ) return;

    champ.split("/").forEach((fragment) => {
      fragment = fragment.trim();

      // ex: "2015-2014-2013"
      if (/^(\d{4}-)+\d{4}$/.test(fragment)) {
        fragment.split("-").forEach((a) => {
          if (/^\d{4}$/.test(a)) annees.add(a);
        });
      }
      // année simple
      else if (/^\d{4}$/.test(fragment)) {
        annees.add(fragment);
      }
      // plage "2014-2015"
      else if (/^\d{4}-\d{4}$/.test(fragment)) {
        const [a1, a2] = fragment.split("-");
        annees.add(a1);
        annees.add(a2);
      }
      // "+14 ans" / "+14ans"
      else if (/^\+?\d+\s?ans$/i.test(fragment)) {
        const num = fragment.match(/\d+/)?.[0];
        if (num) annees.add(`+${num} ans`);
      }
    });
  });

  return Array.from(annees).sort((a, b) => {
    const nA = parseInt(a);
    const nB = parseInt(b);
    if (!isNaN(nA) && !isNaN(nB)) return nB - nA;
    return a.localeCompare(b, "fr");
  });
}

/** Match année (string) sur annees_naissance */
function matchAnnee(annees_naissance, selectedAnnee) {
  if (!selectedAnnee) return true;
  const champ = String(annees_naissance || "");
  const normChamp = champ.replace(/\s+/g, "");
  const normSel = String(selectedAnnee)
    .replace(/\s+/g, "")
    .replace(/ans$/i, "ans");

  const fragments = normChamp
    .split("/")
    .map((frag) => frag.trim())
    .map((frag) => {
      if (/^\d{4}-\d{4}$/.test(frag)) return frag.split("-");
      if (/^\+?\d+ans$/i.test(frag)) {
        const n = frag.match(/\d+/)?.[0];
        return `+${n}ans`;
      }
      return frag;
    })
    .flat()
    .filter(Boolean);

  const sel = normSel.replace(/\s+/g, "");
  const sel2 = sel.replace(/ans$/i, "ans");

  return fragments.some((frag) => frag.includes(sel2));
}

const ORDERED_WEEK_DAYS = [
  "Lundi",
  "Mardi",
  "Mercredi",
  "Jeudi",
  "Vendredi",
  "Samedi",
  "Dimanche",
];

function extractJoursFromGroupes(groupes) {
  const jours = new Set();
  (groupes || []).forEach((g) => {
    for (let i = 1; i <= 6; i++) {
      const horaire = g[`horaire${i}`];
      if (horaire) {
        const [jour] = horaire.split(" ");
        if (jour) jours.add(jour);
      }
    }
  });
  return Array.from(jours).sort(
    (a, b) => ORDERED_WEEK_DAYS.indexOf(a) - ORDERED_WEEK_DAYS.indexOf(b),
  );
}

/* =========================
   UI: Modal détail
   ========================= */

function GroupModal({ open, onClose, group, dojo }) {
  if (!open || !group) return null;

  const base = getHexColor(group.color_groupe);
  const light = lighten(base, 0.72);

  const total =
    (parseFloat(group.tarif_licence || 0) || 0) +
    (parseFloat(group.tarif_cotisation || 0) || 0);

  const percentage =
    group.quota_groupe > 0
      ? Math.round((group.adherentCount / group.quota_groupe) * 100)
      : 0;

  const isFull =
    group.quota_groupe > 0 && group.adherentCount >= group.quota_groupe;
  const info = sanitizeHtml(group.info_groupe);

  const horaires = [...Array(6)]
    .map((_, i) => group[`horaire${i + 1}`])
    .filter(Boolean);

  return (
    <div
      className="fixed inset-0 z-50 bg-black/60 backdrop-blur-sm flex items-center justify-center px-3"
      onClick={onClose}
      role="dialog"
      aria-modal="true"
    >
      <div
        className={cn(
          "relative w-full max-w-3xl overflow-hidden rounded-3xl",
          "bg-white/90 dark:bg-slate-950/80 backdrop-blur",
          "border border-slate-200/60 dark:border-slate-800/70",
          "shadow-2xl",
        )}
        onClick={(e) => e.stopPropagation()}
      >
        <div
          className="p-5 sm:p-7"
          style={{
            background: `linear-gradient(135deg, ${base} 0%, ${light} 100%)`,
          }}
        >
          <button
            onClick={onClose}
            className={cn(
              "absolute right-4 top-4 z-10",
              "h-10 w-10 rounded-full grid place-items-center",
              "bg-white/70 dark:bg-slate-900/60",
              "border border-slate-200/70 dark:border-slate-800",
              "text-slate-700 dark:text-slate-200 hover:text-primary transition",
            )}
            aria-label="Fermer"
          >
            <FiX className="text-xl" />
          </button>

          <div className="text-slate-900">
            <div className="flex flex-wrap gap-2">
              <div className="text-xs font-semibold italic bg-white/60 inline-flex px-3 py-1 rounded-full">
                {dojo?.dojo_nom || "Dojo"}
              </div>

              {group.discipline_groupe ? (
                <div className="text-xs font-semibold bg-white/60 inline-flex px-3 py-1 rounded-full">
                  {group.discipline_groupe}
                </div>
              ) : null}
            </div>

            <h3 className="mt-3 text-2xl sm:text-3xl font-extrabold uppercase">
              {group.nom}
            </h3>
            <div className="mt-1 text-sm font-semibold italic">
              {group.annees_naissance}
            </div>

            <div className="mt-4 flex flex-wrap gap-2">
              {isFull ? (
                <span className="rounded-full bg-rose-600 text-white text-[11px] px-3 py-1 font-bold uppercase shadow">
                  Complet
                </span>
              ) : null}

              <span className="rounded-full bg-white/70 text-slate-900 text-[11px] px-3 py-1 font-bold shadow">
                {group.adherentCount}/{group.quota_groupe} • {percentage}%
                rempli
              </span>

              <span className="rounded-full bg-white/70 text-slate-900 text-[11px] px-3 py-1 font-bold shadow">
                Total annuel : {total}€
              </span>
            </div>

            <div className="mt-4">
              <div className="h-3 w-full rounded-full bg-slate-300/80 dark:bg-slate-700/70 overflow-hidden">
                <div
                  className="h-full rounded-full bg-gradient-to-r from-slate-700 to-slate-400 dark:from-slate-200 dark:to-slate-500 transition-[width] duration-300"
                  style={{ width: `${clamp(percentage, 0, 100)}%` }}
                />
              </div>
            </div>
          </div>
        </div>

        <div className="p-5 sm:p-7 text-slate-900 dark:text-slate-50">
          {dojo?.dojo_adresse ? (
            <p className="text-sm text-slate-700 dark:text-slate-200 flex items-start gap-2">
              <FiMapPin className="mt-0.5 shrink-0" />
              <span>
                <strong>Adresse :</strong> {dojo.dojo_adresse}
              </span>
            </p>
          ) : null}

          <div className="mt-5 grid grid-cols-1 md:grid-cols-2 gap-4 text-sm">
            <div>
              <h4 className="font-semibold mb-2 bg-slate-100 dark:bg-slate-800/60 px-3 py-2 rounded-xl">
                Horaires
              </h4>
              {horaires.length ? (
                <ul className="space-y-1">
                  {horaires.map((h, i) => (
                    <li key={i}>• {h}</li>
                  ))}
                </ul>
              ) : (
                <p className="opacity-80 italic">Non renseigné.</p>
              )}
            </div>

            <div>
              <h4 className="font-semibold mb-2 bg-slate-100 dark:bg-slate-800/60 px-3 py-2 rounded-xl">
                Tarifs
              </h4>
              <p>Licence : {group.tarif_licence} €</p>
              <p>Cotisation : {group.tarif_cotisation} €</p>
              <p className="font-extrabold mt-2">Total : {total} €</p>
            </div>
          </div>

          <div className="mt-4 grid grid-cols-1 md:grid-cols-2 gap-4 text-sm">
            <div>
              <h4 className="font-semibold mb-2 bg-slate-100 dark:bg-slate-800/60 px-3 py-2 rounded-xl">
                Enseignants
              </h4>
              <p>
                {teachersOf(group).join(" • ") || (
                  <span className="italic opacity-80">-</span>
                )}
              </p>
            </div>

            <div>
              <h4 className="font-semibold mb-2 bg-slate-100 dark:bg-slate-800/60 px-3 py-2 rounded-xl">
                Discipline
              </h4>
              <p>{group.discipline_groupe || "-"}</p>
            </div>
          </div>

          <div className="mt-4 grid grid-cols-1 md:grid-cols-2 gap-4 text-sm">
            <div>
              <h4 className="font-semibold mb-2 bg-slate-100 dark:bg-slate-800/60 px-3 py-2 rounded-xl">
                Effectif
              </h4>
              <p>
                {group.adherentCount} inscrit(s) / {group.quota_groupe}{" "}
                <span className="italic opacity-80">
                  ({percentage}% rempli)
                </span>
              </p>
              {group.quota_groupe > 0 &&
              group.adherentCount >= group.quota_groupe ? (
                <div className="mt-2 inline-flex bg-rose-600 text-white text-xs font-bold uppercase px-3 py-1 rounded-full shadow">
                  Complet
                </div>
              ) : null}
            </div>
          </div>

          {group.info_groupe ? (
            <div className="mt-5">
              <h4 className="font-semibold mb-2 bg-slate-100 dark:bg-slate-800/60 px-3 py-2 rounded-xl">
                Informations complémentaires
              </h4>
              <div
                className={cn(
                  "prose prose-slate dark:prose-invert max-w-none",
                  "rounded-2xl border border-slate-200/60 dark:border-slate-800/60",
                  "bg-slate-50/70 dark:bg-slate-900/40 p-4",
                  "dark:[&_strong]:!text-white dark:[&_b]:!text-white",
                  "dark:[&_strong]:font-extrabold dark:[&_b]:font-extrabold",
                )}
                dangerouslySetInnerHTML={{ __html: info }}
              />
            </div>
          ) : null}

          <div className="mt-7 flex justify-end">
            <Button text="Fermer" onClick={onClose} />
          </div>
        </div>
      </div>
    </div>
  );
}

/* =========================
   UI: Mini-card (Vue Groupes)
   ========================= */

function GroupMiniCard({ group, dojoName, onOpen }) {
  const base = getHexColor(group.color_groupe);
  const light = lighten(base, 0.68);
  const titleColor = isDark(base) ? "#fff" : "#0f172a";

  const horaires = [...Array(6)]
    .map((_, i) => group[`horaire${i + 1}`])
    .filter(Boolean);

  const ens = teachersOf(group);

  const isFull =
    group.quota_groupe > 0 && group.adherentCount >= group.quota_groupe;

  return (
    <article
      className={cn(
        "group relative overflow-hidden rounded-3xl",
        "border border-slate-200/70 dark:border-slate-800",
        "bg-white/80 dark:bg-slate-900/60 backdrop-blur",
        "shadow-sm hover:shadow-xl hover:-translate-y-[2px]",
        "transition duration-300 motion-reduce:transition-none",
      )}
    >
      <div
        className="p-4"
        style={{
          background: `linear-gradient(135deg, ${base} 0%, ${light} 100%)`,
        }}
      >
        <div className="flex flex-wrap gap-2">
          <div className="text-[11px] font-semibold bg-white/60 inline-flex px-2.5 py-1 rounded-full dark:text-gray-900">
            {dojoName || "Dojo"}
          </div>

          {group.discipline_groupe ? (
            <div className="text-[11px] font-semibold bg-white/60 inline-flex px-2.5 py-1 rounded-full dark:text-gray-900">
              {group.discipline_groupe}
            </div>
          ) : null}
        </div>

        {isFull ? (
          <span className="absolute right-3 top-3 rounded-full bg-rose-600 text-white text-[10px] px-2.5 py-1 font-bold uppercase shadow">
            Complet
          </span>
        ) : null}

        <h4
          className="mt-3 font-extrabold uppercase tracking-wide leading-snug"
          style={{ color: titleColor }}
        >
          <span className="break-words">{group.nom}</span>
        </h4>

        <div
          className="mt-1 text-xs font-semibold italic"
          style={{ color: titleColor }}
        >
          {group.annees_naissance}
        </div>
      </div>

      <div className="p-4 text-slate-900 dark:text-slate-50">
        <div className="text-xs">
          <span className="font-semibold">Enseignant(s) :</span>{" "}
          <span className="opacity-90">
            {ens.length ? ens.join(" • ") : "-"}
          </span>
        </div>

        <div className="mt-3">
          <div className="text-xs font-semibold mb-1">Horaires :</div>
          {horaires.length ? (
            <ul className="space-y-1.5">
              {horaires.slice(0, 3).map((h, i) => (
                <li key={i} className="flex items-start gap-2 text-xs">
                  <span
                    className="mt-[6px] h-2 w-2 rounded-full shrink-0"
                    style={{ backgroundColor: base }}
                    aria-hidden
                  />
                  <span className="leading-snug">{h}</span>
                </li>
              ))}
              {horaires.length > 3 ? (
                <li className="text-xs italic opacity-70">
                  … +{horaires.length - 3} autres créneaux
                </li>
              ) : null}
            </ul>
          ) : (
            <div className="text-xs italic opacity-70">Non renseigné.</div>
          )}
        </div>

        <div className="mt-4 flex justify-center">
          <button
            onClick={() => onOpen(group)}
            className={cn(
              "rounded-full px-4 py-2 text-sm font-semibold",
              "border border-primary/30 text-primary bg-primary/10 hover:bg-primary/15",
              "transition",
            )}
          >
            Détails
          </button>
        </div>
      </div>

      <div
        aria-hidden
        className="h-1.5 w-full opacity-70 group-hover:opacity-100 transition"
        style={{
          background: `linear-gradient(to right, transparent, ${base}, transparent)`,
        }}
      />
    </article>
  );
}

/* =========================
   UI: Bloc planning (Vue Planning)
   ========================= */

function PlanningChip({ cours, dojoName, onOpen }) {
  const base = getHexColor(cours.color_groupe);
  const light = lighten(base, 0.65);
  const text = isDark(base) ? "#fff" : "#0f172a";

  const isFull =
    cours.quota_groupe > 0 && cours.adherentCount >= cours.quota_groupe;

  return (
    <button
      onClick={() => onOpen(cours)}
      className={cn(
        "relative w-full text-left rounded-2xl shadow-sm hover:shadow-md",
        "transition hover:-translate-y-[1px]",
        "border border-white/60 dark:border-slate-800/60",
      )}
      style={{
        background: `linear-gradient(135deg, ${base} 0%, ${light} 100%)`,
        color: text,
      }}
      title="Voir les détails"
    >
      {isFull ? (
        <span className="absolute right-2 top-2 rounded-full bg-rose-600 text-white text-[10px] px-2 py-1 font-bold uppercase shadow">
          Complet
        </span>
      ) : null}
      <div className="p-3">
        <div className="flex flex-wrap gap-2">
          <div className="text-[11px] font-semibold bg-white/50 inline-flex px-2 py-1 rounded-full text-slate-900">
            {cours.horaire}
          </div>

          {cours.discipline_groupe ? (
            <div className="text-[11px] font-semibold bg-white/50 inline-flex px-2 py-1 rounded-full text-slate-900">
              {cours.discipline_groupe}
            </div>
          ) : null}
        </div>

        <div className="mt-2 font-extrabold uppercase leading-snug">
          <span className="break-words">{cours.nom}</span>
        </div>

        <div className="mt-1 text-xs font-semibold italic opacity-95">
          {cours.annees_naissance}
        </div>

        <div className="mt-2 text-[11px] opacity-95">
          <span className="font-semibold">Dojo :</span> {dojoName || "-"}
        </div>

        <div className="mt-1 text-[11px] opacity-95">
          <span className="font-semibold">Ens. :</span>{" "}
          {teachersOf(cours).slice(0, 2).join(" • ")}
          {teachersOf(cours).length > 2 ? " …" : ""}
        </div>
      </div>
    </button>
  );
}

/* =========================
   Composant principal
   ========================= */

const GroupTable = () => {
  const { user } = useUser();
  const navigate = useNavigate();

  const [groups, setGroups] = useState([]);
  const [dojos, setDojos] = useState([]);
  const [saisonActive, setSaisonActive] = useState("");

  const [selectedDojoId, setSelectedDojoId] = useState(null);
  const [selectedAnnee, setSelectedAnnee] = useState("");
  const [selectedDiscipline, setSelectedDiscipline] = useState("");

  const [view, setView] = useState("groups"); // "groups" | "planning"

  const [showMap, setShowMap] = useState(false);
  const [selectedGroup, setSelectedGroup] = useState(null);
  const [dojoPhotoOpen, setDojoPhotoOpen] = useState(false);

  useEffect(() => {
    if (!dojoPhotoOpen) return;
    const onKey = (e) => {
      if (e.key === "Escape") setDojoPhotoOpen(false);
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [dojoPhotoOpen]);

  useEffect(() => {
    (async () => {
      try {
        const [dojoData, groupsData, adherentsData, settingsData] =
          await Promise.all([
            getDojos(),
            getGroupsHome(),
            getAdherentsByGroup(),
            getSettings(),
          ]);

        const combined = (groupsData || []).map((g) => {
          const info = (adherentsData || []).find((ad) => ad.nom === g.nom);
          return {
            ...g,
            adherentCount: info?.adherentCount || 0,
            quota_groupe: info?.quota_groupe || 0,
          };
        });

        const actifs = combined.filter((g) =>
          typeof g.is_active === "boolean" ? g.is_active : true,
        );

        setDojos(Array.isArray(dojoData) ? dojoData : []);
        if (dojoData?.length) setSelectedDojoId(dojoData[0].id);
        setGroups(Array.isArray(actifs) ? actifs : []);
        setSaisonActive(settingsData?.[0]?.saison_active || "");
      } catch (e) {
        console.error("Erreur lors du chargement :", e);
      }
    })();
  }, []);

  const selectedDojo = useMemo(
    () => dojos.find((d) => d.id === selectedDojoId) || null,
    [dojos, selectedDojoId],
  );

  const mapUrl = useMemo(() => {
    if (!selectedDojo?.dojo_adresse) return "";
    return `https://www.google.com/maps?q=${encodeURIComponent(
      selectedDojo.dojo_adresse,
    )}&output=embed`;
  }, [selectedDojo?.dojo_adresse]);

  const listeAnnees = useMemo(() => extractAnneesUnique(groups), [groups]);
  const listeDisciplines = useMemo(
    () => extractDisciplinesUnique(groups),
    [groups],
  );

  const filteredGroups = useMemo(() => {
    let base = selectedDojoId
      ? groups.filter((g) => g.dojo_id === selectedDojoId)
      : groups;

    if (selectedDiscipline) {
      base = base.filter(
        (g) =>
          normalizeDiscipline(g.discipline_groupe) ===
          normalizeDiscipline(selectedDiscipline),
      );
    }

    if (selectedAnnee) {
      base = base.filter((g) => matchAnnee(g.annees_naissance, selectedAnnee));
    }

    return base;
  }, [groups, selectedDojoId, selectedDiscipline, selectedAnnee]);

  const joursUtilises = useMemo(
    () => extractJoursFromGroupes(filteredGroups),
    [filteredGroups],
  );

  const getCoursParJour = (jour) => {
    const result = [];
    filteredGroups.forEach((g) => {
      for (let i = 1; i <= 6; i++) {
        const h = g[`horaire${i}`];
        if (h && h.startsWith(jour)) {
          const parts = h.split(" ");
          const plage = parts.slice(1).join(" ").trim();
          result.push({ ...g, horaire: plage || h });
        }
      }
    });
    return result.sort((a, b) =>
      (a.horaire || "").localeCompare(b.horaire || "", "fr"),
    );
  };

  const dojoNameOf = (dojoId) =>
    dojos.find((d) => d.id === dojoId)?.dojo_nom || "";

  const handleButtonClick = () => {
    navigate(user ? "/ajouter-inscription-adherent" : "/register");
  };

  const buttonText = user
    ? "Faire une inscription ou cours d'essai"
    : "S'inscrire pour faire une adhésion";

  const dojoForModal = useMemo(() => {
    if (!selectedGroup) return null;
    return dojos.find((d) => d.id === selectedGroup.dojo_id) || selectedDojo;
  }, [selectedGroup, dojos, selectedDojo]);

  return (
    <section
      className={cn(
        "relative py-12 sm:py-16 px-4",
        "bg-lightBackground dark:bg-darkBackground",
        "text-black dark:text-white",
      )}
      id="groupes"
    >
      <div className="mx-auto max-w-7xl">
        <SectionTitle
          title="Dojos • Groupes • Horaires • Tarifs"
          subtitle={saisonActive ? `Saison : ${saisonActive}` : ""}
        />

        <div className="mt-8 flex flex-wrap justify-center gap-2">
          <button
            onClick={() => {
              setSelectedDojoId(null);
              setShowMap(false);
            }}
            className={cn(
              "px-4 py-2 rounded-full font-semibold text-sm border transition",
              selectedDojoId === null
                ? "bg-primary/10 text-primary border-primary/30"
                : "bg-white/70 dark:bg-slate-900/50 text-slate-800 dark:text-slate-100 border-slate-200 dark:border-slate-800 hover:bg-slate-50 dark:hover:bg-slate-800/60",
            )}
          >
            Tous les dojos
          </button>

          {dojos.map((dojo) => (
            <button
              key={dojo.id}
              onClick={() => {
                setSelectedDojoId(dojo.id);
                setShowMap(false);
              }}
              className={cn(
                "px-4 py-2 rounded-full font-semibold text-sm border transition",
                selectedDojoId === dojo.id
                  ? "bg-primary/10 text-primary border-primary/30"
                  : "bg-white/70 dark:bg-slate-900/50 text-slate-800 dark:text-slate-100 border-slate-200 dark:border-slate-800 hover:bg-slate-50 dark:hover:bg-slate-800/60",
              )}
            >
              {dojo.dojo_nom}
            </button>
          ))}
        </div>

        {selectedDojoId && selectedDojo ? (
          <div
            className={cn(
              "mt-8 mx-auto max-w-5xl overflow-hidden rounded-3xl",
              "border border-slate-200/70 dark:border-slate-800",
              "bg-white/75 dark:bg-slate-900/60 backdrop-blur shadow-sm",
            )}
          >
            <div className="grid md:grid-cols-12 gap-0">
              <div className="md:col-span-4">
                <button
                  type="button"
                  onClick={() => setDojoPhotoOpen(true)}
                  className="block w-full h-full"
                  aria-label="Agrandir la photo du dojo"
                >
                  <img
                    src={
                      toAbsolute(selectedDojo.dojo_photoURL) ||
                      "/img/no-image.jpg"
                    }
                    alt={selectedDojo.dojo_nom}
                    className="h-44 md:h-full w-full object-cover cursor-zoom-in"
                    loading="lazy"
                    onError={(e) => {
                      e.currentTarget.src = "/img/no-image.jpg";
                    }}
                  />
                </button>
              </div>

              <div className="md:col-span-8 p-5">
                <div className="flex items-start justify-between gap-4">
                  <div className="min-w-0">
                    <h3 className="text-xl font-extrabold">
                      {selectedDojo.dojo_nom}
                    </h3>
                    <p className="text-sm text-slate-600 dark:text-slate-300">
                      {selectedDojo.dojo_discipline}
                    </p>
                    {selectedDojo.dojo_adresse ? (
                      <p className="mt-3 text-sm text-slate-700 dark:text-slate-200 flex items-start gap-2">
                        <FiMapPin className="mt-0.5 shrink-0" />
                        <span>{selectedDojo.dojo_adresse}</span>
                      </p>
                    ) : null}
                  </div>

                  {mapUrl ? (
                    <button
                      onClick={() => setShowMap((v) => !v)}
                      className="text-sm font-semibold text-primary hover:underline shrink-0"
                    >
                      {showMap ? "Masquer la carte" : "Afficher la carte"}
                    </button>
                  ) : null}
                </div>

                {showMap && mapUrl ? (
                  <div className="mt-4">
                    <iframe
                      title={`Map ${selectedDojo.dojo_nom}`}
                      src={mapUrl}
                      width="100%"
                      height="220"
                      className="rounded-2xl border border-slate-200/70 dark:border-slate-800"
                      loading="lazy"
                    />
                  </div>
                ) : null}
              </div>
            </div>
          </div>
        ) : null}

        <div className="mt-6 flex justify-center">
          <div
            className={cn(
              "inline-flex rounded-full p-1",
              "border border-slate-200/70 dark:border-slate-800",
              "bg-white/70 dark:bg-slate-900/50 backdrop-blur",
            )}
          >
            <button
              onClick={() => setView("groups")}
              className={cn(
                "px-4 py-2 rounded-full text-sm font-semibold transition",
                view === "groups"
                  ? "bg-primary text-white shadow"
                  : "text-slate-700 dark:text-slate-200 hover:bg-slate-100/70 dark:hover:bg-slate-800/60",
              )}
            >
              Vue Groupes
            </button>
            <button
              onClick={() => setView("planning")}
              className={cn(
                "px-4 py-2 rounded-full text-sm font-semibold transition",
                view === "planning"
                  ? "bg-primary text-white shadow"
                  : "text-slate-700 dark:text-slate-200 hover:bg-slate-100/70 dark:hover:bg-slate-800/60",
              )}
            >
              Vue Planning
            </button>
          </div>
        </div>

        <div
          className={cn(
            "mt-6 mx-auto max-w-4xl",
            "rounded-3xl border border-slate-200/70 dark:border-slate-800",
            "bg-white/75 dark:bg-slate-900/60 backdrop-blur shadow-sm",
          )}
        >
          <div className="p-4 sm:p-5 grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="block text-xs font-semibold mb-1 text-slate-600 dark:text-slate-300">
                Filtrer par discipline
              </label>
              <select
                className={cn(
                  "w-full border border-slate-200/70 dark:border-slate-800 rounded-2xl px-3 py-2",
                  "bg-white/70 dark:bg-slate-900/60",
                  "focus:outline-none focus:ring-2 focus:ring-primary/40",
                )}
                value={selectedDiscipline}
                onChange={(e) => setSelectedDiscipline(e.target.value)}
              >
                <option value="">Toutes les disciplines</option>
                {listeDisciplines.map((d) => (
                  <option key={d} value={d}>
                    {d}
                  </option>
                ))}
              </select>
            </div>

            <div>
              <label className="block text-xs font-semibold mb-1 text-slate-600 dark:text-slate-300">
                Filtrer par année de naissance
              </label>
              <select
                className={cn(
                  "w-full border border-slate-200/70 dark:border-slate-800 rounded-2xl px-3 py-2",
                  "bg-white/70 dark:bg-slate-900/60",
                  "focus:outline-none focus:ring-2 focus:ring-primary/40",
                )}
                value={selectedAnnee}
                onChange={(e) => setSelectedAnnee(e.target.value)}
              >
                <option value="">Toutes les années</option>
                {listeAnnees.map((a) => (
                  <option key={a} value={a}>
                    {a}
                  </option>
                ))}
              </select>
            </div>

            <div className="md:col-span-2 flex flex-wrap items-center gap-2 text-xs text-slate-500 dark:text-slate-300">
              <span>{filteredGroups.length} groupe(s) trouvé(s)</span>

              {(selectedDiscipline || selectedAnnee) && (
                <button
                  type="button"
                  onClick={() => {
                    setSelectedDiscipline("");
                    setSelectedAnnee("");
                  }}
                  className="inline-flex items-center rounded-full px-3 py-1 border border-slate-200 dark:border-slate-700 hover:bg-slate-100 dark:hover:bg-slate-800 transition"
                >
                  Réinitialiser les filtres
                </button>
              )}
            </div>
          </div>
        </div>

        {view === "groups" ? (
          <div className="mt-10">
            <h3 className="text-base sm:text-lg font-extrabold uppercase tracking-wide mb-4">
              Groupes
            </h3>

            <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-4">
              {filteredGroups.map((g) => (
                <GroupMiniCard
                  key={g.id}
                  group={g}
                  dojoName={dojoNameOf(g.dojo_id)}
                  onOpen={setSelectedGroup}
                />
              ))}
            </div>
          </div>
        ) : null}

        {view === "planning" ? (
          <div className="mt-10">
            <h3 className="text-base sm:text-lg font-extrabold uppercase tracking-wide mb-4">
              Planning des cours
            </h3>

            <div className="grid grid-cols-2 md:grid-cols-4 gap-6">
              {joursUtilises.map((jour) => (
                <div key={jour}>
                  <h4 className="text-lg md:text-xl font-extrabold uppercase border-b border-slate-200/70 dark:border-slate-800 pb-2 mb-4">
                    {jour}
                  </h4>

                  <div className="space-y-3">
                    {getCoursParJour(jour).map((cours, idx) => (
                      <PlanningChip
                        key={`${jour}-${cours.id}-${idx}`}
                        cours={cours}
                        dojoName={dojoNameOf(cours.dojo_id)}
                        onOpen={setSelectedGroup}
                      />
                    ))}
                  </div>
                </div>
              ))}
            </div>
          </div>
        ) : null}

        <div className="mt-10 flex justify-center">
          <Button
            text={buttonText}
            onClick={handleButtonClick}
            className="mx-auto"
          />
        </div>
      </div>

      {dojoPhotoOpen && selectedDojo ? (
        <div
          className="fixed inset-0 z-[60] bg-black/70 backdrop-blur-sm flex items-center justify-center p-4"
          onClick={() => setDojoPhotoOpen(false)}
          role="dialog"
          aria-modal="true"
        >
          <div
            className="relative w-full max-w-5xl"
            onClick={(e) => e.stopPropagation()}
          >
            <button
              type="button"
              onClick={() => setDojoPhotoOpen(false)}
              className={cn(
                "absolute -top-3 -right-3 z-10",
                "h-10 w-10 rounded-full grid place-items-center",
                "bg-white/80 dark:bg-slate-900/70",
                "border border-slate-200/70 dark:border-slate-800",
                "text-slate-700 dark:text-slate-200 hover:text-primary transition",
              )}
              aria-label="Fermer"
            >
              <FiX className="text-xl" />
            </button>

            <img
              src={
                toAbsolute(selectedDojo.dojo_photoURL) || "/img/no-image.jpg"
              }
              alt={selectedDojo.dojo_nom}
              className={cn(
                "w-full h-auto max-h-[80vh] object-contain",
                "rounded-3xl shadow-2xl",
                "border border-white/20",
              )}
              onError={(e) => {
                e.currentTarget.src = "/img/no-image.jpg";
              }}
            />

            <div className="mt-3 text-center text-white/90 text-sm font-semibold">
              {selectedDojo.dojo_nom}
            </div>
          </div>
        </div>
      ) : null}

      <GroupModal
        open={Boolean(selectedGroup)}
        onClose={() => setSelectedGroup(null)}
        group={selectedGroup}
        dojo={dojoForModal}
      />
    </section>
  );
};

export default GroupTable;